/**
 * Finance sources — cash, P&L, working capital, balance sheet and the
 * finance-side forecasts. Everything here is gated to the `financials`
 * module (super admin only; see `src/lib/permissions.ts`).
 *
 * Cash direction follows the convention the Financials pages use for
 * `v_daily_cash_flow`: `credit` rows are money in, `debit` rows money out.
 */

import type { BankAccountLite, DatasetBag } from '../datasets';
import type { BreakdownDef, ForecastSourceDef, MetricDef, TableDef, TableRowData } from '../catalogTypes';
import type { SeriesPoint } from '../types';
import { currentMonthKey, eachBucket, monthKey, rollup, sliceWindowMonths, todayIso } from '../series';
import {
  dailySeries,
  groupSum,
  inWindow,
  monthlySeries,
  subtractSeries,
  sum,
  toSeries,
  toSlices,
} from './helpers';

const MODULE = 'financials';
const COGS_PATTERN = /cost of goods|cost of sales|\bcogs\b/i;

function isCreditAccount(account: BankAccountLite): boolean {
  return (account.account_type ?? '').toLowerCase().includes('credit');
}

function depositAccounts(bag: DatasetBag): BankAccountLite[] {
  return (bag.bank ?? []).filter((a) => !isCreditAccount(a));
}

function cashNow(bag: DatasetBag): number | null {
  const accounts = depositAccounts(bag);
  if (accounts.length === 0) return null;
  return sum(accounts.map((a) => a.current_balance_cents));
}

function cashInDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(
    (bag.cashflow_daily ?? []).filter((r) => r.direction === 'credit'),
    (r) => r.day,
    (r) => r.amount_cents,
  );
}

function cashOutDaily(bag: DatasetBag): SeriesPoint[] {
  return dailySeries(
    (bag.cashflow_daily ?? []).filter((r) => r.direction === 'debit'),
    (r) => r.day,
    (r) => r.amount_cents,
  );
}

function netFlowDaily(bag: DatasetBag): SeriesPoint[] {
  return subtractSeries(cashInDaily(bag), cashOutDaily(bag));
}

/**
 * Reconstructs an end-of-day cash balance series by walking today's
 * balance backwards through the daily net flows. Exact for the days the
 * flow dataset covers; the first point is the balance at the start of it.
 */
function cashBalanceDaily(bag: DatasetBag): SeriesPoint[] {
  const now = cashNow(bag);
  if (now == null) return [];
  const flows = netFlowDaily(bag);
  const today = todayIso();
  const firstDay = flows[0]?.t ?? today;
  const days = eachBucket(firstDay < today ? firstDay : today, today, 'day');
  const flowBy = new Map(flows.map((p) => [p.t, p.v]));
  const out: SeriesPoint[] = [];
  let balance = now;
  for (let i = days.length - 1; i >= 0; i--) {
    out.push({ t: days[i], v: balance });
    balance -= flowBy.get(days[i]) ?? 0;
  }
  return out.reverse();
}

function cashBalanceMonthly(bag: DatasetBag): SeriesPoint[] {
  return rollup(cashBalanceDaily(bag), 'month', 'last');
}

function pnlRows(bag: DatasetBag, type: 'revenue' | 'expense') {
  return (bag.pnl_monthly ?? []).filter((r) => r.account_type === type);
}

function revenueMonthly(bag: DatasetBag): SeriesPoint[] {
  return monthlySeries(pnlRows(bag, 'revenue'), (r) => r.month, (r) => r.amount_cents);
}

function expensesMonthly(bag: DatasetBag): SeriesPoint[] {
  return monthlySeries(pnlRows(bag, 'expense'), (r) => r.month, (r) => r.amount_cents);
}

function cogsRows(bag: DatasetBag) {
  return pnlRows(bag, 'expense').filter((r) => COGS_PATTERN.test(r.account_name));
}

function cogsMonthly(bag: DatasetBag): SeriesPoint[] {
  return monthlySeries(cogsRows(bag), (r) => r.month, (r) => r.amount_cents);
}

function netIncomeMonthly(bag: DatasetBag): SeriesPoint[] {
  return subtractSeries(revenueMonthly(bag), expensesMonthly(bag));
}

function planRows(bag: DatasetBag, type: 'revenue' | 'expense') {
  return (bag.projections_pnl ?? []).filter((r) => r.account_type === type);
}

function planRevenueMonthly(bag: DatasetBag): SeriesPoint[] {
  return monthlySeries(planRows(bag, 'revenue'), (r) => r.month, (r) => r.amount_cents);
}

function planExpensesMonthly(bag: DatasetBag): SeriesPoint[] {
  return monthlySeries(planRows(bag, 'expense'), (r) => r.month, (r) => r.amount_cents);
}

function planCashMonthly(bag: DatasetBag, direction: 'credit' | 'debit'): SeriesPoint[] {
  return monthlySeries(
    (bag.projections_cashflow ?? []).filter((r) => r.direction === direction),
    (r) => r.txn_date,
    (r) => r.amount_cents,
  );
}

function planNetCashMonthly(bag: DatasetBag): SeriesPoint[] {
  return subtractSeries(planCashMonthly(bag, 'credit'), planCashMonthly(bag, 'debit'));
}

/** Average monthly net outflow over the last three complete months (positive = burning). */
function recentBurn(bag: DatasetBag): number | null {
  const monthly = rollup(netFlowDaily(bag), 'month', 'sum');
  const current = currentMonthKey();
  const complete = monthly.filter((p) => p.t < current).slice(-3);
  if (complete.length === 0) return null;
  const avgNet = sum(complete.map((p) => p.v)) / complete.length;
  return -avgNet;
}

function lastCompleteMonths(points: SeriesPoint[], count: number): SeriesPoint[] {
  const current = currentMonthKey();
  return points.filter((p) => p.t < current).slice(-count);
}

function arTotal(bag: DatasetBag): number | null {
  if (!bag.ar_aging) return null;
  return sum(bag.ar_aging.map((r) => r.total_open_cents));
}

function arOverdue(bag: DatasetBag): number | null {
  if (!bag.ar_aging) return null;
  return sum(
    bag.ar_aging.map(
      (r) => r.bucket_0_30_cents + r.bucket_31_60_cents + r.bucket_61_90_cents + r.bucket_over_90_cents,
    ),
  );
}

function apTotal(bag: DatasetBag): number | null {
  if (!bag.ap_aging) return null;
  return sum(bag.ap_aging.map((r) => r.total_open_cents));
}

function apOverdue(bag: DatasetBag): number | null {
  if (!bag.ap_aging) return null;
  return sum(
    bag.ap_aging.map(
      (r) => r.bucket_0_30_cents + r.bucket_31_60_cents + r.bucket_61_90_cents + r.bucket_over_90_cents,
    ),
  );
}

function balanceTotal(bag: DatasetBag, type: 'asset' | 'liability' | 'equity'): number | null {
  if (!bag.balance_sheet) return null;
  return sum(bag.balance_sheet.filter((r) => r.account_type === type).map((r) => r.balance_cents));
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const FINANCE_METRICS: MetricDef[] = [
  {
    id: 'fin.cash_on_hand',
    kind: 'metric',
    label: 'Cash on hand',
    group: 'Finance',
    description: 'Combined balance of deposit bank accounts (credit lines excluded).',
    module: MODULE,
    requires: ['bank', 'cashflow_daily'],
    unit: 'currency',
    direction: 'up',
    grain: 'day',
    agg: 'last',
    series: cashBalanceDaily,
    snapshot: cashNow,
    available: (bag) => (bag.bank && depositAccounts(bag).length === 0 ? 'No bank accounts connected yet.' : null),
  },
  {
    id: 'fin.cash_in',
    kind: 'metric',
    label: 'Cash in',
    group: 'Finance',
    description: 'Deposits and incoming transfers across all bank accounts.',
    module: MODULE,
    requires: ['cashflow_daily'],
    unit: 'currency',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: cashInDaily,
    nonNegative: true,
  },
  {
    id: 'fin.cash_out',
    kind: 'metric',
    label: 'Cash out',
    group: 'Finance',
    description: 'Payments and outgoing transfers across all bank accounts.',
    module: MODULE,
    requires: ['cashflow_daily'],
    unit: 'currency',
    direction: 'down',
    grain: 'day',
    agg: 'sum',
    series: cashOutDaily,
    nonNegative: true,
  },
  {
    id: 'fin.net_cash_flow',
    kind: 'metric',
    label: 'Net cash flow',
    group: 'Finance',
    description: 'Cash in minus cash out.',
    module: MODULE,
    requires: ['cashflow_daily'],
    unit: 'currency',
    direction: 'up',
    grain: 'day',
    agg: 'sum',
    series: netFlowDaily,
  },
  {
    id: 'fin.burn_rate',
    kind: 'metric',
    label: 'Monthly burn',
    group: 'Finance',
    description: 'Average net cash outflow per month (zero in months that were cash-positive).',
    module: MODULE,
    requires: ['cashflow_daily'],
    unit: 'currency',
    direction: 'down',
    grain: 'month',
    agg: 'avg',
    series: (bag) => rollup(netFlowDaily(bag), 'month', 'sum').map((p) => ({ t: p.t, v: Math.max(0, -p.v) })),
    nonNegative: true,
  },
  {
    id: 'fin.runway',
    kind: 'metric',
    label: 'Runway',
    group: 'Finance',
    description: 'Months of cash left at the average burn of the last three complete months.',
    module: MODULE,
    requires: ['bank', 'cashflow_daily'],
    unit: 'months',
    direction: 'up',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      const cash = cashNow(bag);
      const burn = recentBurn(bag);
      if (cash == null || burn == null || burn <= 0) return null;
      return cash / burn;
    },
    available: (bag) => {
      if (!bag.bank || !bag.cashflow_daily) return null;
      const burn = recentBurn(bag);
      if (burn == null) return 'Needs at least one complete month of bank activity.';
      if (burn <= 0) return 'Cash flow was positive over the last three months — no burn to measure.';
      return null;
    },
  },
  {
    id: 'fin.revenue',
    kind: 'metric',
    label: 'Revenue (P&L)',
    group: 'Finance',
    description: 'Booked revenue by month from the QuickBooks profit & loss.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    direction: 'up',
    grain: 'month',
    agg: 'sum',
    series: revenueMonthly,
    nonNegative: true,
  },
  {
    id: 'fin.expenses',
    kind: 'metric',
    label: 'Expenses (P&L)',
    group: 'Finance',
    description: 'Booked expenses by month, including cost of goods.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    direction: 'down',
    grain: 'month',
    agg: 'sum',
    series: expensesMonthly,
    nonNegative: true,
  },
  {
    id: 'fin.cogs',
    kind: 'metric',
    label: 'Cost of goods sold',
    group: 'Finance',
    description: 'Expense accounts named "Cost of goods sold" / "Cost of sales".',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    direction: 'down',
    grain: 'month',
    agg: 'sum',
    series: cogsMonthly,
    nonNegative: true,
    available: (bag) =>
      bag.pnl_monthly && cogsRows(bag).length === 0 ? 'No cost-of-goods accounts found in the chart of accounts.' : null,
  },
  {
    id: 'fin.gross_profit',
    kind: 'metric',
    label: 'Gross profit',
    group: 'Finance',
    description: 'Revenue minus cost of goods sold.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    direction: 'up',
    grain: 'month',
    agg: 'sum',
    series: (bag) => subtractSeries(revenueMonthly(bag), cogsMonthly(bag)),
    available: (bag) =>
      bag.pnl_monthly && cogsRows(bag).length === 0 ? 'No cost-of-goods accounts found in the chart of accounts.' : null,
  },
  {
    id: 'fin.gross_margin',
    kind: 'metric',
    label: 'Gross margin',
    group: 'Finance',
    description: 'Gross profit as a share of revenue.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'percent',
    direction: 'up',
    grain: 'month',
    agg: 'sum',
    ratio: { num: 'fin.gross_profit', den: 'fin.revenue' },
    available: (bag) =>
      bag.pnl_monthly && cogsRows(bag).length === 0 ? 'No cost-of-goods accounts found in the chart of accounts.' : null,
  },
  {
    id: 'fin.net_income',
    kind: 'metric',
    label: 'Net income',
    group: 'Finance',
    description: 'Revenue minus all expenses.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    direction: 'up',
    grain: 'month',
    agg: 'sum',
    series: netIncomeMonthly,
  },
  {
    id: 'fin.net_margin',
    kind: 'metric',
    label: 'Net margin',
    group: 'Finance',
    description: 'Net income as a share of revenue.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'percent',
    direction: 'up',
    grain: 'month',
    agg: 'sum',
    ratio: { num: 'fin.net_income', den: 'fin.revenue' },
  },
  {
    id: 'fin.expense_ratio',
    kind: 'metric',
    label: 'Expense ratio',
    group: 'Finance',
    description: 'Expenses as a share of revenue — below 100% means profitable.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'percent',
    direction: 'down',
    grain: 'month',
    agg: 'sum',
    ratio: { num: 'fin.expenses', den: 'fin.revenue' },
  },
  {
    id: 'fin.plan_revenue',
    kind: 'metric',
    label: 'Planned revenue',
    group: 'Finance',
    description: 'Revenue in the base-scenario P&L projection.',
    module: MODULE,
    requires: ['projections_pnl'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'month',
    agg: 'sum',
    series: planRevenueMonthly,
    nonNegative: true,
  },
  {
    id: 'fin.plan_expenses',
    kind: 'metric',
    label: 'Planned expenses',
    group: 'Finance',
    description: 'Expenses in the base-scenario P&L projection.',
    module: MODULE,
    requires: ['projections_pnl'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'month',
    agg: 'sum',
    series: planExpensesMonthly,
    nonNegative: true,
  },
  {
    id: 'fin.revenue_vs_plan',
    kind: 'metric',
    label: 'Revenue vs plan',
    group: 'Finance',
    description: 'Booked revenue as a share of planned revenue for the same months.',
    module: MODULE,
    requires: ['pnl_monthly', 'projections_pnl'],
    unit: 'percent',
    direction: 'up',
    grain: 'month',
    agg: 'sum',
    ratio: { num: 'fin.revenue', den: 'fin.plan_revenue' },
  },
  {
    id: 'fin.ar_open',
    kind: 'metric',
    label: 'Accounts receivable',
    group: 'Finance',
    description: 'Open customer invoices.',
    module: MODULE,
    requires: ['ar_aging'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: arTotal,
  },
  {
    id: 'fin.ar_overdue',
    kind: 'metric',
    label: 'Overdue receivables',
    group: 'Finance',
    description: 'Customer invoices past their due date.',
    module: MODULE,
    requires: ['ar_aging'],
    unit: 'currency',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: arOverdue,
  },
  {
    id: 'fin.ar_overdue_share',
    kind: 'metric',
    label: 'Receivables overdue %',
    group: 'Finance',
    description: 'Share of open receivables that are past due.',
    module: MODULE,
    requires: ['ar_aging'],
    unit: 'percent',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      const total = arTotal(bag);
      const overdue = arOverdue(bag);
      return total == null || overdue == null || total === 0 ? null : overdue / total;
    },
  },
  {
    id: 'fin.dso',
    kind: 'metric',
    label: 'Days sales outstanding',
    group: 'Finance',
    description: 'Open receivables divided by average daily revenue over the last three complete months.',
    module: MODULE,
    requires: ['ar_aging', 'pnl_monthly'],
    unit: 'days',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      const ar = arTotal(bag);
      const months = lastCompleteMonths(revenueMonthly(bag), 3);
      const revenue = sum(months.map((p) => p.v));
      if (ar == null || months.length === 0 || revenue <= 0) return null;
      return ar / (revenue / (months.length * 30.4));
    },
  },
  {
    id: 'fin.ap_open',
    kind: 'metric',
    label: 'Accounts payable',
    group: 'Finance',
    description: 'Open vendor bills.',
    module: MODULE,
    requires: ['ap_aging'],
    unit: 'currency',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: apTotal,
  },
  {
    id: 'fin.ap_overdue',
    kind: 'metric',
    label: 'Overdue payables',
    group: 'Finance',
    description: 'Vendor bills past their due date.',
    module: MODULE,
    requires: ['ap_aging'],
    unit: 'currency',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: apOverdue,
  },
  {
    id: 'fin.dpo',
    kind: 'metric',
    label: 'Days payable outstanding',
    group: 'Finance',
    description: 'Open payables divided by average daily expenses over the last three complete months.',
    module: MODULE,
    requires: ['ap_aging', 'pnl_monthly'],
    unit: 'days',
    direction: 'neutral',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      const ap = apTotal(bag);
      const months = lastCompleteMonths(expensesMonthly(bag), 3);
      const expenses = sum(months.map((p) => p.v));
      if (ap == null || months.length === 0 || expenses <= 0) return null;
      return ap / (expenses / (months.length * 30.4));
    },
  },
  {
    id: 'fin.working_capital_gap',
    kind: 'metric',
    label: 'Receivables − payables',
    group: 'Finance',
    description: 'Net trade position: what customers owe you minus what you owe vendors.',
    module: MODULE,
    requires: ['ar_aging', 'ap_aging'],
    unit: 'currency',
    direction: 'up',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      const ar = arTotal(bag);
      const ap = apTotal(bag);
      return ar == null || ap == null ? null : ar - ap;
    },
  },
  {
    id: 'fin.total_assets',
    kind: 'metric',
    label: 'Total assets',
    group: 'Finance',
    description: 'Balance sheet assets.',
    module: MODULE,
    requires: ['balance_sheet'],
    unit: 'currency',
    direction: 'up',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => balanceTotal(bag, 'asset'),
  },
  {
    id: 'fin.total_liabilities',
    kind: 'metric',
    label: 'Total liabilities',
    group: 'Finance',
    description: 'Balance sheet liabilities.',
    module: MODULE,
    requires: ['balance_sheet'],
    unit: 'currency',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => balanceTotal(bag, 'liability'),
  },
  {
    id: 'fin.equity',
    kind: 'metric',
    label: 'Equity',
    group: 'Finance',
    description: 'Balance sheet equity.',
    module: MODULE,
    requires: ['balance_sheet'],
    unit: 'currency',
    direction: 'up',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => balanceTotal(bag, 'equity'),
  },
  {
    id: 'fin.debt_to_equity',
    kind: 'metric',
    label: 'Debt to equity',
    group: 'Finance',
    description: 'Total liabilities divided by equity.',
    module: MODULE,
    requires: ['balance_sheet'],
    unit: 'multiple',
    direction: 'down',
    grain: 'snapshot',
    agg: 'last',
    snapshot: (bag) => {
      const liabilities = balanceTotal(bag, 'liability');
      const equity = balanceTotal(bag, 'equity');
      return liabilities == null || equity == null || equity <= 0 ? null : liabilities / equity;
    },
  },
];

// ---------------------------------------------------------------------------
// Breakdowns
// ---------------------------------------------------------------------------

function agingBuckets(rows: Array<{
  not_due_cents: number;
  bucket_0_30_cents: number;
  bucket_31_60_cents: number;
  bucket_61_90_cents: number;
  bucket_over_90_cents: number;
}>) {
  return [
    { label: 'Not yet due', value: sum(rows.map((r) => r.not_due_cents)) },
    { label: '1–30 days', value: sum(rows.map((r) => r.bucket_0_30_cents)) },
    { label: '31–60 days', value: sum(rows.map((r) => r.bucket_31_60_cents)) },
    { label: '61–90 days', value: sum(rows.map((r) => r.bucket_61_90_cents)) },
    { label: 'Over 90 days', value: sum(rows.map((r) => r.bucket_over_90_cents)) },
  ].filter((s) => s.value !== 0);
}

export const FINANCE_BREAKDOWNS: BreakdownDef[] = [
  {
    id: 'fin.revenue_by_account',
    kind: 'breakdown',
    label: 'Revenue by account',
    group: 'Finance',
    description: 'P&L revenue accounts for the months in the window.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          pnlRows(bag, 'revenue').filter((r) => r.month >= monthKey(start) && r.month <= monthKey(end)),
          (r) => r.account_name,
          (r) => r.amount_cents,
        ),
      ),
  },
  {
    id: 'fin.expenses_by_account',
    kind: 'breakdown',
    label: 'Expenses by account',
    group: 'Finance',
    description: 'P&L expense accounts for the months in the window.',
    module: MODULE,
    requires: ['pnl_monthly'],
    unit: 'currency',
    slices: (bag, start, end) =>
      toSlices(
        groupSum(
          pnlRows(bag, 'expense').filter((r) => r.month >= monthKey(start) && r.month <= monthKey(end)),
          (r) => r.account_name,
          (r) => r.amount_cents,
        ),
      ),
  },
  {
    id: 'fin.cash_by_account',
    kind: 'breakdown',
    label: 'Cash by bank account',
    group: 'Finance',
    description: 'Current balance of each deposit account.',
    module: MODULE,
    requires: ['bank'],
    unit: 'currency',
    slices: (bag) => toSlices(groupSum(depositAccounts(bag), (a) => a.name, (a) => a.current_balance_cents)),
  },
  {
    id: 'fin.ar_by_customer',
    kind: 'breakdown',
    label: 'Receivables by customer',
    group: 'Finance',
    description: 'Open invoice balance per customer.',
    module: MODULE,
    requires: ['ar_aging'],
    unit: 'currency',
    slices: (bag) =>
      toSlices(groupSum(bag.ar_aging ?? [], (r) => r.customer_name ?? 'Unknown customer', (r) => r.total_open_cents)),
  },
  {
    id: 'fin.ap_by_vendor',
    kind: 'breakdown',
    label: 'Payables by vendor',
    group: 'Finance',
    description: 'Open bill balance per vendor.',
    module: MODULE,
    requires: ['ap_aging'],
    unit: 'currency',
    slices: (bag) =>
      toSlices(groupSum(bag.ap_aging ?? [], (r) => r.vendor_name ?? 'Unknown vendor', (r) => r.total_open_cents)),
  },
  {
    id: 'fin.ar_aging_buckets',
    kind: 'breakdown',
    label: 'Receivables aging',
    group: 'Finance',
    description: 'Open receivables by how far past due they are.',
    module: MODULE,
    requires: ['ar_aging'],
    unit: 'currency',
    slices: (bag) => agingBuckets(bag.ar_aging ?? []),
  },
  {
    id: 'fin.ap_aging_buckets',
    kind: 'breakdown',
    label: 'Payables aging',
    group: 'Finance',
    description: 'Open payables by how far past due they are.',
    module: MODULE,
    requires: ['ap_aging'],
    unit: 'currency',
    slices: (bag) => agingBuckets(bag.ap_aging ?? []),
  },
  {
    id: 'fin.cash_in_vs_out',
    kind: 'breakdown',
    label: 'Cash in vs out',
    group: 'Finance',
    description: 'Total inflows and outflows for the window.',
    module: MODULE,
    requires: ['cashflow_daily'],
    unit: 'currency',
    slices: (bag, start, end) => {
      const rows = (bag.cashflow_daily ?? []).filter((r) => inWindow(r.day, start, end));
      return [
        { label: 'Cash in', value: sum(rows.filter((r) => r.direction === 'credit').map((r) => r.amount_cents)) },
        { label: 'Cash out', value: sum(rows.filter((r) => r.direction === 'debit').map((r) => r.amount_cents)) },
      ].filter((s) => s.value !== 0);
    },
  },
];

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

const AGING_COLUMNS = (who: string, countKey: string, countLabel: string) => [
  { key: 'name', label: who },
  { key: 'not_due', label: 'Not due', unit: 'currency' as const },
  { key: 'b0', label: '1–30', unit: 'currency' as const },
  { key: 'b31', label: '31–60', unit: 'currency' as const },
  { key: 'b61', label: '61–90', unit: 'currency' as const },
  { key: 'b90', label: '90+', unit: 'currency' as const },
  { key: 'total', label: 'Open', unit: 'currency' as const },
  { key: countKey, label: countLabel, unit: 'count' as const },
  { key: 'max_days', label: 'Max days late', unit: 'days' as const },
];

export const FINANCE_TABLES: TableDef[] = [
  {
    id: 'fin.ar_aging_table',
    kind: 'table',
    label: 'Receivables aging by customer',
    group: 'Finance',
    description: 'Who owes you money and how late it is.',
    module: MODULE,
    requires: ['ar_aging'],
    columns: AGING_COLUMNS('Customer', 'invoices', 'Invoices'),
    defaultColumns: ['name', 'b0', 'b31', 'b61', 'b90', 'total'],
    defaultSort: { key: 'total', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag) =>
      (bag.ar_aging ?? []).map((r, i) => ({
        key: r.customer_id ?? r.qb_customer_id ?? String(i),
        cells: {
          name: r.customer_name ?? 'Unknown customer',
          not_due: r.not_due_cents,
          b0: r.bucket_0_30_cents,
          b31: r.bucket_31_60_cents,
          b61: r.bucket_61_90_cents,
          b90: r.bucket_over_90_cents,
          total: r.total_open_cents,
          invoices: r.invoice_count,
          max_days: r.max_days_past_due,
        },
      })),
  },
  {
    id: 'fin.ap_aging_table',
    kind: 'table',
    label: 'Payables aging by vendor',
    group: 'Finance',
    description: 'Who you owe and how late it is.',
    module: MODULE,
    requires: ['ap_aging'],
    columns: AGING_COLUMNS('Vendor', 'bills', 'Bills'),
    defaultColumns: ['name', 'b0', 'b31', 'b61', 'b90', 'total'],
    defaultSort: { key: 'total', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag) =>
      (bag.ap_aging ?? []).map((r, i) => ({
        key: r.vendor_id ?? r.qb_vendor_id ?? String(i),
        cells: {
          name: r.vendor_name ?? 'Unknown vendor',
          not_due: r.not_due_cents,
          b0: r.bucket_0_30_cents,
          b31: r.bucket_31_60_cents,
          b61: r.bucket_61_90_cents,
          b90: r.bucket_over_90_cents,
          total: r.total_open_cents,
          bills: r.bill_count,
          max_days: r.max_days_past_due,
        },
      })),
  },
  {
    id: 'fin.bank_accounts_table',
    kind: 'table',
    label: 'Bank accounts',
    group: 'Finance',
    description: 'Current balance per account.',
    module: MODULE,
    requires: ['bank'],
    columns: [
      { key: 'name', label: 'Account' },
      { key: 'type', label: 'Type' },
      { key: 'balance', label: 'Balance', unit: 'currency', signed: true },
    ],
    defaultSort: { key: 'balance', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag) =>
      (bag.bank ?? []).map((a) => ({
        key: a.id,
        cells: { name: a.name, type: a.account_type ?? '—', balance: a.current_balance_cents },
      })),
  },
  {
    id: 'fin.pnl_by_month',
    kind: 'table',
    label: 'P&L by month',
    group: 'Finance',
    description: 'Revenue, expenses, net income and margin for each month in the window.',
    module: MODULE,
    requires: ['pnl_monthly'],
    columns: [
      { key: 'month', label: 'Month', date: true },
      { key: 'revenue', label: 'Revenue', unit: 'currency' },
      { key: 'expenses', label: 'Expenses', unit: 'currency' },
      { key: 'net', label: 'Net income', unit: 'currency', signed: true },
      { key: 'margin', label: 'Margin', unit: 'percent', signed: true },
    ],
    defaultSort: { key: 'month', dir: 'desc' },
    defaultLimit: 12,
    rows: (bag, start, end) => {
      const revenue = new Map(sliceWindowMonths(revenueMonthly(bag), start, end).map((p) => [p.t, p.v]));
      const expenses = new Map(sliceWindowMonths(expensesMonthly(bag), start, end).map((p) => [p.t, p.v]));
      const months = Array.from(new Set([...revenue.keys(), ...expenses.keys()])).sort();
      return months.map((m) => {
        const rev = revenue.get(m) ?? 0;
        const exp = expenses.get(m) ?? 0;
        return {
          key: m,
          cells: { month: m, revenue: rev, expenses: exp, net: rev - exp, margin: rev === 0 ? null : (rev - exp) / rev },
        };
      });
    },
  },
  {
    id: 'fin.cash_flow_by_month',
    kind: 'table',
    label: 'Cash flow by month',
    group: 'Finance',
    description: 'Inflows, outflows and net cash movement per month in the window.',
    module: MODULE,
    requires: ['cashflow_daily'],
    columns: [
      { key: 'month', label: 'Month', date: true },
      { key: 'in', label: 'Cash in', unit: 'currency' },
      { key: 'out', label: 'Cash out', unit: 'currency' },
      { key: 'net', label: 'Net', unit: 'currency', signed: true },
    ],
    defaultSort: { key: 'month', dir: 'desc' },
    defaultLimit: 12,
    rows: (bag, start, end) => {
      const rows = (bag.cashflow_daily ?? []).filter((r) => inWindow(r.day, start, end));
      const inflow = toSeries(groupSum(rows.filter((r) => r.direction === 'credit'), (r) => monthKey(r.day), (r) => r.amount_cents));
      const outflow = toSeries(groupSum(rows.filter((r) => r.direction === 'debit'), (r) => monthKey(r.day), (r) => r.amount_cents));
      const outBy = new Map(outflow.map((p) => [p.t, p.v]));
      const inBy = new Map(inflow.map((p) => [p.t, p.v]));
      const months = Array.from(new Set([...inBy.keys(), ...outBy.keys()])).sort();
      return months.map<TableRowData>((m) => {
        const i = inBy.get(m) ?? 0;
        const o = outBy.get(m) ?? 0;
        return { key: m, cells: { month: m, in: i, out: o, net: i - o } };
      });
    },
  },
  {
    id: 'fin.expense_accounts_table',
    kind: 'table',
    label: 'Expenses by account',
    group: 'Finance',
    description: 'Each expense account for the months in the window, with its share of the total.',
    module: MODULE,
    requires: ['pnl_monthly'],
    columns: [
      { key: 'account', label: 'Account' },
      { key: 'amount', label: 'Amount', unit: 'currency' },
      { key: 'share', label: 'Share', unit: 'percent' },
    ],
    defaultSort: { key: 'amount', dir: 'desc' },
    defaultLimit: 10,
    rows: (bag, start, end) => {
      const rows = pnlRows(bag, 'expense').filter((r) => r.month >= monthKey(start) && r.month <= monthKey(end));
      const byAccount = groupSum(rows, (r) => r.account_name, (r) => r.amount_cents);
      const total = sum(Array.from(byAccount.values()));
      return Array.from(byAccount.entries()).map(([account, amount]) => ({
        key: account,
        cells: { account, amount, share: total === 0 ? null : amount / total },
      }));
    },
  },
];

// ---------------------------------------------------------------------------
// Forecast sources
// ---------------------------------------------------------------------------

function plannedCashBalance(bag: DatasetBag): SeriesPoint[] {
  const now = cashNow(bag);
  if (now == null) return [];
  const net = planNetCashMonthly(bag).filter((p) => p.t >= currentMonthKey());
  let balance = now;
  return net.map((p) => {
    balance += p.v;
    return { t: p.t, v: balance };
  });
}

export const FINANCE_FORECASTS: ForecastSourceDef[] = [
  {
    id: 'fc.revenue',
    kind: 'forecast',
    label: 'Revenue (P&L)',
    group: 'Finance',
    description: 'Booked monthly revenue projected forward, with the P&L plan for comparison.',
    module: MODULE,
    requires: ['pnl_monthly', 'projections_pnl'],
    unit: 'currency',
    nonNegative: true,
    history: revenueMonthly,
    manual: planRevenueMonthly,
    manualLabel: 'Plan',
  },
  {
    id: 'fc.expenses',
    kind: 'forecast',
    label: 'Expenses (P&L)',
    group: 'Finance',
    description: 'Booked monthly expenses projected forward, with the P&L plan for comparison.',
    module: MODULE,
    requires: ['pnl_monthly', 'projections_pnl'],
    unit: 'currency',
    nonNegative: true,
    history: expensesMonthly,
    manual: planExpensesMonthly,
    manualLabel: 'Plan',
  },
  {
    id: 'fc.net_income',
    kind: 'forecast',
    label: 'Net income',
    group: 'Finance',
    description: 'Monthly net income projected forward; the plan line is planned revenue minus planned expenses.',
    module: MODULE,
    requires: ['pnl_monthly', 'projections_pnl'],
    unit: 'currency',
    nonNegative: false,
    history: netIncomeMonthly,
    manual: (bag) => subtractSeries(planRevenueMonthly(bag), planExpensesMonthly(bag)),
    manualLabel: 'Plan',
  },
  {
    id: 'fc.cash_balance',
    kind: 'forecast',
    label: 'Cash balance',
    group: 'Finance',
    description:
      'Month-end cash walked forward from today’s balance using the projected net cash flow. Flags the month cash would run out.',
    module: MODULE,
    requires: ['bank', 'cashflow_daily', 'projections_cashflow'],
    unit: 'currency',
    nonNegative: false,
    fill: 'carry',
    history: cashBalanceMonthly,
    balance: {
      startValue: cashNow,
      flow: (bag) => rollup(netFlowDaily(bag), 'month', 'sum'),
    },
    manual: plannedCashBalance,
    manualLabel: 'Planned balance',
  },
  {
    id: 'fc.cash_in',
    kind: 'forecast',
    label: 'Cash in',
    group: 'Finance',
    description: 'Monthly bank inflows projected forward, with the cash-flow plan for comparison.',
    module: MODULE,
    requires: ['cashflow_daily', 'projections_cashflow'],
    unit: 'currency',
    nonNegative: true,
    history: (bag) => rollup(cashInDaily(bag), 'month', 'sum'),
    manual: (bag) => planCashMonthly(bag, 'credit'),
    manualLabel: 'Plan',
  },
  {
    id: 'fc.cash_out',
    kind: 'forecast',
    label: 'Cash out',
    group: 'Finance',
    description: 'Monthly bank outflows projected forward, with the cash-flow plan for comparison.',
    module: MODULE,
    requires: ['cashflow_daily', 'projections_cashflow'],
    unit: 'currency',
    nonNegative: true,
    history: (bag) => rollup(cashOutDaily(bag), 'month', 'sum'),
    manual: (bag) => planCashMonthly(bag, 'debit'),
    manualLabel: 'Plan',
  },
  {
    id: 'fc.net_cash_flow',
    kind: 'forecast',
    label: 'Net cash flow',
    group: 'Finance',
    description: 'Monthly net cash movement projected forward.',
    module: MODULE,
    requires: ['cashflow_daily', 'projections_cashflow'],
    unit: 'currency',
    nonNegative: false,
    history: (bag) => rollup(netFlowDaily(bag), 'month', 'sum'),
    manual: planNetCashMonthly,
    manualLabel: 'Plan',
  },
];
